import { Component, HostListener, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatTableDataSource } from '@angular/material/table'
import { Router } from '@angular/router'
import { AddonsAccessService } from 'src/app/addons/addons-subscribtion/addons-access.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { LimitModalComponent } from 'src/app/free-plan/limit-modal/limit-modal.component'
import { PointsService } from '../points.service'
import { environment } from 'src/environments/environment'

export interface PointsSetup {
  id: string
  points_system?: string
  sub_mode?: string
  enable_points?: boolean
  [key: string]: any
}

@Component({
  selector: 'app-points-list',
  templateUrl: './points-list.component.html',
  styleUrls: ['./points-list.component.scss']
})
export class PointsListComponent implements OnInit {
  dataSource = new MatTableDataSource<PointsSetup>()
  searchTerm: string = ''
  project_id = this.builderService.selectedProject
  currentView: 'points' | 'users' | 'customers' = 'points'
  addonName = 'Points and Rewards'
  showGracePeriodWarning = false
  gracePeriodInfo: any = null

  // Pagination state
  pageIndex = 0
  pageSize = 5

  // Delete modal state
  showDeleteModal = false
  pointToDeleteId: string | null = null

  // Filter state
  selectedFilter = 'all'
  showFilterDropdown = false

  constructor(
    public builderService: BuilderService,
    private pointsService: PointsService,
    public router: Router,
    private addonsAccess: AddonsAccessService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.addonsAccess.loadSubscriptions()
    this.addonsAccess.getSubscriptions$().subscribe(() => {
      this.checkGracePeriod()
    })
    this.loadPointsSetups()
  }

  @HostListener('document:click')
  closeDropdowns() {
    this.showFilterDropdown = false
  }

  checkGracePeriod() {
    if (this.addonsAccess.isAddonInGracePeriod(this.addonName)) {
      this.gracePeriodInfo = this.addonsAccess.getGracePeriodInfo(this.addonName)
      this.showGracePeriodWarning = true
    } else {
      this.showGracePeriodWarning = false
    }
  }

  validatePoints() {
    if (this.addonsAccess.isAddonActive('All Addons Bundle') || this.addonsAccess.isAddonActive('Points and Rewards')) {
      this.router.navigate(['/points/:id'])
      return
    }

    this.openLockedModal()
  }

  openLockedModal() {
    const dialogRef = this.dialog.open(LimitModalComponent, {
      width: '337px',
      maxWidth: '90vw',
      panelClass: 'limit-dialog',
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: false,
      data: {
        title: 'Unlock Points & Rewards',
        message: 'Points & rewards are locked on your plan.',
        cta: 'Unlock Points & Rewards'
      }
    })

  dialogRef.afterClosed().subscribe((upgrade) => {
    if (upgrade) {
      const token = localStorage.getItem('token')
      const params = new URLSearchParams()
      if (token) params.set('token', token)
      params.set('project_id', encodeURIComponent(this.builderService.selectedProject))
      window.open(`${environment.stacksURL}addons/?${params.toString()}`, '_blank')
    }
  })
}


  /**
   * Load points setups from the database
   */
  loadPointsSetups() {
    this.pointsService.getPointsSetups().subscribe((data: any[]) => {
      if (!data || !data.length) {
        this.dataSource.data = []
        return
      }

      const isActive = this.isPointsAddonActive()
      const pointsArray: PointsSetup[] = data.map((row: any) => {
        const point = { ...row }
        // 🔒 FORCE disable if addon not active
        if (!isActive && point.enable_points) {
          point.enable_points = false
          // Persist forced disable — errors are logged inside PointsService/SupabaseService.
          this.pointsService.setPointsEnabled(point.id, false).subscribe()
        }

        return point
      })
      this.dataSource.data = pointsArray
    })
  }

  /**
   * Delete a points setup by ID
   */
  openDeleteModal(setupId: string) {
    this.pointToDeleteId = setupId
    this.showDeleteModal = true
  }

  closeDeleteModal() {
    this.showDeleteModal = false
    this.pointToDeleteId = null
  }

  deletePoints(setupId: string) {
    if (confirm('Are you sure you want to delete this Points setup?')) {
      this.pointsService.deletePointsSetup(setupId).subscribe(() => {
        this.loadPointsSetups()
        this.closeDeleteModal()
      })
    }
  }

  /**
   * Filter the data in the table
   */
  applyFilter(filterValue: string) {
    this.searchTerm = filterValue
    this.pageIndex = 0
  }

  isPointsAddonActive(): boolean {
    return this.addonsAccess.isAddonActive('Points and Rewards') || this.addonsAccess.isAddonActive('All Addons Bundle')
  }


  /**
   * Toggle the enable_points value for a specific Points setup.
   * @param point The PointsSetup object (row in the table).
   * @param newStatus The boolean value from the slide-toggle (true or false).
   */
  toggleEnablePoints(point: PointsSetup, newStatus: boolean) {
    this.pointsService.setPointsEnabled(point.id, newStatus).subscribe(() => {
      point.enable_points = newStatus
    })
  }

  /**
   * Check if we have any points setups
   */
  hasPoints(): boolean {
    return this.dataSource.data.length > 0
  }

  // ─── Filter Dropdown ───────────────────────────────────────────────────────

  toggleFilterDropdown(event: Event) {
    event.stopPropagation()
    this.showFilterDropdown = !this.showFilterDropdown
  }

  selectFilter(filter: string) {
    this.selectedFilter = filter
    this.showFilterDropdown = false
    this.pageIndex = 0
  }

  getFilterLabel(filter: string): string {
    const labels: Record<string, string> = {
      byOrder: 'By Order',
      byUnit: 'Currency',
      byProductCategory: 'Product / Category',
      byReferral: 'Referral'
    }
    return labels[filter] || filter
  }

  // ─── Pagination ────────────────────────────────────────────────────────────

  get processedPoints(): PointsSetup[] {
    let data = this.dataSource.data
    const term = this.searchTerm.trim().toLowerCase()
    if (term) {
      data = data.filter((p) =>
        p.id?.toLowerCase().includes(term) ||
        p.points_system?.toLowerCase().includes(term)
      )
    }
    if (this.selectedFilter !== 'all') {
      data = data.filter((p) => p.points_system === this.selectedFilter)
    }
    return data
  }

  get displayedPoints(): PointsSetup[] {
    const start = this.pageIndex * this.pageSize
    return this.processedPoints.slice(start, start + this.pageSize)
  }

  getTotalPages(): number {
    return Math.ceil(this.processedPoints.length / this.pageSize) || 1
  }

  previousPage() {
    if (this.pageIndex > 0) this.pageIndex--
  }

  nextPage() {
    if (this.pageIndex < this.getTotalPages() - 1) this.pageIndex++
  }
}
